import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { formatEther, formatUnits } from 'viem'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { deadmanSwitch } from '@/services/index.ts'

export function AdminTreasury() {
  const { t } = useTranslation()
  const [tokenBalance, setTokenBalance] = useState<bigint | null>(null)
  const [ethBalance, setEthBalance] = useState<bigint | null>(null)
  const [tokenInfo, setTokenInfo] = useState<{
    symbol: string
    decimals: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [withdrawing, setWithdrawing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [tokenBal, ethBal, info] = await Promise.all([
        deadmanSwitch.getContractTokenBalance(),
        deadmanSwitch.getContractEthBalance(),
        deadmanSwitch.getTokenInfo(),
      ])
      setTokenBalance(tokenBal)
      setEthBalance(ethBal)
      setTokenInfo({ symbol: info.symbol, decimals: info.decimals })
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleWithdraw = async () => {
    setWithdrawing(true)
    setError(null)
    try {
      await deadmanSwitch.withdrawFees()
      await load()
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    } finally {
      setWithdrawing(false)
    }
  }

  if (loading) {
    return <p className="text-fg-muted">{t('common.loading')}</p>
  }

  if (error && tokenBalance === null && ethBalance === null) {
    return (
      <div className="text-error">
        {t('admin.treasuryError')}: {error}
      </div>
    )
  }

  const tokenFormatted =
    tokenBalance != null && tokenInfo
      ? formatUnits(tokenBalance, tokenInfo.decimals)
      : '0'
  const ethFormatted = ethBalance != null ? formatEther(ethBalance) : '0'

  return (
    <div className="flex flex-col gap-8">
      {error && <div className="text-error">{error}</div>}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-6">
        <div className="border-line bg-surface-raised flex flex-col gap-2 rounded-lg border p-8">
          <span className="text-fg-secondary text-sm">
            {t('admin.treasuryTokenBalance')} {tokenInfo?.symbol ?? ''}
          </span>
          <span className="text-fg font-mono text-xl font-semibold">
            {tokenFormatted}
          </span>
        </div>
        <div className="border-line bg-surface-raised flex flex-col gap-2 rounded-lg border p-8">
          <span className="text-fg-secondary text-sm">
            {t('admin.treasuryEthBalance')}
          </span>
          <span className="text-fg font-mono text-xl font-semibold">
            {ethFormatted} ETH
          </span>
        </div>
      </div>
      <div className="flex gap-4">
        <button
          type="button"
          className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleWithdraw}
          disabled={withdrawing}
        >
          {withdrawing ? t('common.loading') : t('admin.treasuryWithdraw')}
        </button>
      </div>
    </div>
  )
}
